/**
 * Review panel for automated redaction.
 *
 * Detection and destruction are deliberately separate steps. The operator sees
 * every match, what pattern found it, and which page it is on, and chooses what
 * to destroy. Redaction is irreversible in the saved file, so a confirmation
 * that shows the actual values is the minimum owed to whoever has to sign off
 * on the release.
 */

import { useEffect, useMemo, useState } from 'react'
import type { RedactionRegion } from '../api'

interface Props {
  open: boolean
  regions: RedactionRegion[]
  onCancel: () => void
  onApply: (regions: RedactionRegion[]) => void
}

const LABELS: Record<string, string> = {
  sin: 'Social Insurance Number',
  credit_card: 'Payment card',
  email: 'Email address',
  phone: 'Telephone',
  postal_code: 'Postal code',
  date_of_birth: 'Date of birth',
  drivers_licence: "Driver's licence",
  manual: 'Manual selection',
}

export function RedactionPanel({ open, regions, onCancel, onApply }: Props) {
  const [chosen, setChosen] = useState<Set<number>>(new Set())

  // Default to everything selected: the safe default for a privacy tool is to
  // remove what it found, not to quietly leave it in.
  useEffect(() => {
    if (open) setChosen(new Set(regions.map((_, index) => index)))
  }, [open, regions])

  const byReason = useMemo(() => {
    const groups = new Map<string, number[]>()
    regions.forEach((region, index) => {
      const list = groups.get(region.reason) ?? []
      list.push(index)
      groups.set(region.reason, list)
    })
    return [...groups.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [regions])

  if (!open) return null

  const toggle = (index: number) =>
    setChosen((previous) => {
      const next = new Set(previous)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })

  const toggleGroup = (indices: number[]) =>
    setChosen((previous) => {
      const next = new Set(previous)
      const allOn = indices.every((i) => next.has(i))
      indices.forEach((i) => (allOn ? next.delete(i) : next.add(i)))
      return next
    })

  const selected = regions.filter((_, index) => chosen.has(index))

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Review redactions">
      <div className="modal__panel modal__panel--wide">
        <h2 className="modal__title">
          Review redactions &mdash; {regions.length} match
          {regions.length === 1 ? '' : 'es'}
        </h2>

        <div className="modal__body">
          {regions.length === 0 ? (
            <p className="modal__hint">Nothing matched on any page.</p>
          ) : (
            <>
              <div className="redact-groups">
                {byReason.map(([reason, indices]) => (
                  <button
                    key={reason}
                    className="chip"
                    data-on={indices.every((i) => chosen.has(i))}
                    onClick={() => toggleGroup(indices)}
                    title={`Toggle all ${indices.length} ${reason} match(es)`}
                  >
                    {LABELS[reason] ?? reason} ({indices.length})
                  </button>
                ))}
              </div>

              <div className="redact-list" role="list">
                {regions.map((region, index) => (
                  <label className="redact-row" key={index} role="listitem">
                    <input
                      type="checkbox"
                      checked={chosen.has(index)}
                      onChange={() => toggle(index)}
                    />
                    <span className="redact-row__page">
                      p{region.pageIndex + 1}
                    </span>
                    <span className="redact-row__reason">
                      {LABELS[region.reason] ?? region.reason}
                    </span>
                    <code className="redact-row__text">
                      {region.text || '(area)'}
                    </code>
                  </label>
                ))}
              </div>

              <p className="modal__hint modal__hint--warn">
                Redaction destroys the characters in the content stream and the
                pixels in any image underneath. It is <strong>not reversible</strong>{' '}
                once saved, and Save will flatten the file. Odie re-reads the
                result afterwards and refuses to release it if anything is still
                recoverable.
              </p>
            </>
          )}
        </div>

        <div className="modal__footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn--danger"
            disabled={selected.length === 0}
            onClick={() => onApply(selected)}
          >
            Redact {selected.length} region{selected.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  )
}
